import { useField } from 'formik';
import { ReactElement, useCallback } from 'react';

type WithFormikProps<P, T> = P & { value: T; onChange: (value: T) => void };
type WithFormikReturnType<P> = Omit<P, 'value' | 'onChange'> & { name: string };

export function withFormik<P, T>(
	component: (props: WithFormikProps<P, T>) => ReactElement,
): (props: WithFormikReturnType<P>) => ReactElement {
	const WithFormik = (props: WithFormikReturnType<P>) => {
		const { name, ...rest } = props;
		const [{ value }, , { setValue, setTouched }] = useField(name);

		const Component = component;

		const onChange = useCallback(
			(value: T) => {
				setValue(value);
			},
			[setValue],
		);

		const onBlur = useCallback(() => {
			setTouched(true);
		}, [setTouched]);

		return (
			// @ts-expect-error: This was already like this, so we accept the possibility
			// P' could be instantiated with an arbitrary type which could be unrelated to 'Omit, "name"> & { value: any; onChange: (value: T) => void; onBlur: () => void; }
			<Component {...rest} value={value} onChange={onChange} onBlur={onBlur} />
		);
	};

	WithFormik.displayName = 'withFormik';

	return WithFormik;
}
